import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOffer, formatCountdown } from "@/lib/useOffer";
import { toast } from "sonner";
import { Check, Lock, Loader2, Clock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const TERMS_URL = "https://misteracademy.it/termini.html";
const PRIVACY_URL = "https://www.iubenda.com/privacy-policy/36827680";

const features = [
  "5 simulazioni ufficiali complete (300 domande)",
  "Tutte le lezioni: mappe concettuali e riepiloghi",
  "Ripasso mirato degli errori, blocco per blocco",
  "Simulatore d'esame con regola reale 9/15 per blocco",
  "Accesso completo per 12 mesi dall'acquisto",
];

export default function Sblocca() {
  const { user } = useAuth();
  const { offer, refetchOffer } = useOffer();
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [withdrawalWaived, setWithdrawalWaived] = useState(false);
  const [, setTick] = useState(0); // forza il refresh del countdown ogni minuto
  const expired = user?.subscriptionStatus === "expired";
  const canPay = termsAccepted && withdrawalWaived;
  const offerActive = !!offer?.attiva;
  const countdown = offerActive ? formatCountdown(offer.scade_il) : null;

  // Aggiorna il conto alla rovescia (ore:minuti) finché l'offerta è attiva
  useEffect(() => {
    if (!offerActive) return;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [offerActive]);

  const startCheckout = async () => {
    if (!canPay) return;
    setLoading(true);
    try {
      // 1) Salva il consenso lato server (prova legale). Se fallisce, NON si procede.
      await api.post("/payments/consent", { termsAccepted, withdrawalWaived });
    } catch (e) {
      toast.error(errMsg(e, "Impossibile registrare il consenso. Riprova."));
      setLoading(false);
      return;
    }
    try {
      // 2) Solo dopo il salvataggio riuscito: redirect a Stripe (early-bird se offerta attiva)
      const endpoint = offerActive ? "/checkout/early-bird" : "/payments/checkout";
      const r = await api.post(endpoint, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      if (e?.response?.status === 403) {
        // Offerta scaduta tra il caricamento e il clic: torna al prezzo pieno
        toast.error("L'offerta è scaduta");
        await refetchOffer();
        setLoading(false);
        return;
      }
      toast.error(errMsg(e, "Impossibile avviare il pagamento. Riprova."));
      setLoading(false);
    }
  };

  if (user?.hasFullAccess) {
    return (
      <div className="pt-10 max-w-md mx-auto text-center fade-up">
        <span className="inline-flex w-12 h-12 rounded-full bg-[color:var(--volt)] items-center justify-center" aria-hidden="true">
          <Check className="w-6 h-6" strokeWidth={3} />
        </span>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight" data-testid="unlock-already-active">Accesso completo attivo.</h1>
        <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">Hai già tutto: simulazioni, lezioni e ripasso errori. Buon allenamento.</p>
        <Link
          to="/"
          data-testid="unlock-back-home-button"
          className="press mt-6 inline-block rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
        >
          Vai alla home
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-6 max-w-md mx-auto">
      <div className="fade-up d1">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--text-2)] border border-[color:var(--hairline)] rounded-full px-3 py-1">
          <Lock className="w-3 h-3" aria-hidden="true" /> {expired ? "Accesso scaduto" : "Accesso completo"}
        </span>
        {expired ? (
          <>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight leading-tight" data-testid="unlock-expired-title">
              Il tuo accesso<br />è scaduto.
            </h1>
            <p className="mt-3 text-sm text-[color:var(--text-2)] leading-6" data-testid="unlock-expired-notice">
              I 12 mesi del tuo accesso completo sono terminati. Rinnova con un nuovo pagamento unico e riprendi
              ad allenarti da dove eri rimasto: i tuoi progressi e risultati sono salvati.
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight leading-tight">
              Tutto il percorso.<br />Un solo pagamento.
            </h1>
            <p className="mt-3 text-sm text-[color:var(--text-2)] leading-6">
              Pagamento unico, nessun abbonamento. Sblocchi tutto e ti alleni per 12 mesi dall'acquisto.
            </p>
          </>
        )}
      </div>

      <div className="mt-6 rounded-3xl bg-[color:var(--ink)] text-white p-6 fade-up d2" data-testid="unlock-price-card">
        {offerActive && (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-2xl bg-[color:var(--volt)] text-[color:var(--ink)] px-3 py-2" data-testid="unlock-earlybird-banner">
            <span className="font-mono text-[10px] font-extrabold tracking-[0.14em] uppercase">Offerta early bird</span>
            {countdown && (
              <span className="inline-flex items-center gap-1 font-mono text-xs font-bold" data-testid="unlock-countdown">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {countdown}
              </span>
            )}
          </div>
        )}
        <div className="flex items-baseline gap-2">
          {offerActive ? (
            <>
              <span className="font-mono text-5xl font-semibold tracking-tight" data-testid="unlock-price">29&euro;</span>
              <span className="font-mono text-xl text-white/40 line-through" data-testid="unlock-price-original">39&euro;</span>
              <span className="font-mono text-[11px] tracking-widest uppercase text-white/50">una tantum</span>
            </>
          ) : (
            <>
              <span className="font-mono text-5xl font-semibold tracking-tight" data-testid="unlock-price">39&euro;</span>
              <span className="font-mono text-[11px] tracking-widest uppercase text-white/50">una tantum</span>
            </>
          )}
        </div>
        <ul className="mt-5 space-y-3">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm leading-5">
              <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[color:var(--volt)] flex items-center justify-center" aria-hidden="true">
                <Check className="w-2.5 h-2.5 text-[color:var(--ink)]" strokeWidth={4} />
              </span>
              {f}
            </li>
          ))}
        </ul>
        {/* Consenso legale obbligatorio — checkbox NON pre-selezionate */}
        <div className="mt-6 space-y-3.5 border-t border-white/10 pt-5">
          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="consent-terms">
            <Checkbox
              id="consent-terms"
              checked={termsAccepted}
              onCheckedChange={(v) => setTermsAccepted(v === true)}
              data-testid="consent-terms-checkbox"
              className="mt-0.5 shrink-0 w-5 h-5 border-white/40 data-[state=checked]:bg-[color:var(--volt)] data-[state=checked]:border-[color:var(--volt)] data-[state=checked]:text-[color:var(--ink)]"
            />
            <span className="text-[13px] leading-5 text-white/80">
              Ho letto e accetto i{" "}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="consent-terms-link"
                className="underline underline-offset-2 font-semibold text-white hover:text-[color:var(--volt)] transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                Termini &amp; Condizioni
              </a>{" "}
              e la{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="consent-privacy-link"
                className="underline underline-offset-2 font-semibold text-white hover:text-[color:var(--volt)] transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="consent-waiver">
            <Checkbox
              id="consent-waiver"
              checked={withdrawalWaived}
              onCheckedChange={(v) => setWithdrawalWaived(v === true)}
              data-testid="consent-waiver-checkbox"
              className="mt-0.5 shrink-0 w-5 h-5 border-white/40 data-[state=checked]:bg-[color:var(--volt)] data-[state=checked]:border-[color:var(--volt)] data-[state=checked]:text-[color:var(--ink)]"
            />
            <span className="text-[13px] leading-5 text-white/80">
              Chiedo di accedere immediatamente ai contenuti e sono consapevole che, trattandosi di contenuto
              digitale ad accesso immediato, perderò il diritto di recesso di 14 giorni una volta iniziata la fruizione.
            </span>
          </label>
        </div>

        <button
          onClick={startCheckout}
          disabled={loading || !canPay}
          data-testid="unlock-checkout-button"
          className="press mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--volt)] text-[color:var(--ink)] font-extrabold px-6 py-4 min-h-[52px] hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Reindirizzamento…
            </>
          ) : offerActive ? (
            "Sblocca a 29 €"
          ) : (
            "Sblocca ora — 39 €"
          )}
        </button>
        <p className="mt-3 text-center text-[11px] text-white/40">Pagamento sicuro con Stripe. Carte di credito e debito.</p>
      </div>

      <p className="mt-5 text-xs text-[color:var(--text-2)] leading-5 fade-up d3">
        L'accesso è personale e vale 12 mesi dall'acquisto. Per assistenza contatta l'amministratore del corso.
      </p>
    </div>
  );
}
